import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import ClientLayout from '../../components/layout/ClientLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { HardHat, Shield, Store, ArrowLeft, Plus, Trash2, Send, CalendarRange } from 'lucide-react';
import RangeGenerator from '../../components/client/RangeGenerator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: 'labour', label: 'Labour', icon: HardHat, desc: 'Warehouse, production, cleaning & general labour staff' },
  { key: 'security', label: 'Security', icon: Shield, desc: 'SIA door supervisors, CCTV, stewards & event security' },
  { key: 'retail', label: 'Retail', icon: Store, desc: 'Retail security & loss prevention staff' },
];

const SITE_LABEL = { labour: 'Site / Warehouse Name', security: 'Event Name', retail: 'Venue Name' };

const EMPTY_DAY = { date: '', start_time: '', end_time: '', position: '', staff_required: 1, notes: '' };

export default function ClientNewRequestPage() {
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [positions, setPositions] = useState([]);
  const [form, setForm] = useState({
    site_name: '', address: '', postcode: '', contact_person: '', contact_number: '',
    purchase_order: '', reference_number: '', additional_info: '',
  });
  const [days, setDays] = useState([{ ...EMPTY_DAY }]);
  const [submitting, setSubmitting] = useState(false);
  const [rangeOpen, setRangeOpen] = useState(false);

  const loadPositions = useCallback(async (cat) => {
    try {
      const res = await axios.get(`${API}/staffing-positions?category=${cat}`);
      setPositions(res.data || []);
    } catch {
      setPositions([]);
    }
  }, []);

  useEffect(() => { if (category) loadPositions(category); }, [category, loadPositions]);

  const setDay = (i, patch) => setDays((prev) => prev.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  const addDay = () => setDays((prev) => [...prev, { ...EMPTY_DAY, ...(prev.length ? {
    start_time: prev[prev.length - 1].start_time,
    end_time: prev[prev.length - 1].end_time,
    position: prev[prev.length - 1].position,
  } : {}) }]);
  const removeDay = (i) => setDays((prev) => prev.filter((_, idx) => idx !== i));

  const applyGeneratedRows = (rows) => {
    setDays((prev) => {
      const onlyBlank = prev.length === 1 && !prev[0].date && !prev[0].start_time && !prev[0].position;
      return onlyBlank ? rows : [...prev, ...rows];
    });
    setRangeOpen(false);
    toast.success(`${rows.length} row${rows.length !== 1 ? 's' : ''} added — adjust any row individually if needed`);
  };

  const totalStaff = days.reduce((s, d) => s + (parseInt(d.staff_required, 10) || 0), 0);

  const submit = async () => {
    for (const [f, label] of [['site_name', SITE_LABEL[category]], ['address', 'Full Address'], ['postcode', 'Postcode'], ['contact_person', 'Contact Person'], ['contact_number', 'Contact Number']]) {
      if (!form[f].trim()) { toast.error(`${label} is required`); return; }
    }
    for (let i = 0; i < days.length; i++) {
      const d = days[i];
      if (!d.date || !d.start_time || !d.end_time || !d.position || !(parseInt(d.staff_required, 10) >= 1)) {
        toast.error(`Day ${i + 1}: complete date, times, position and staff number`);
        return;
      }
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/client/staffing-requests`, {
        category,
        ...form,
        purchase_order: form.purchase_order || null,
        reference_number: form.reference_number || null,
        additional_info: form.additional_info || null,
        days: days.map((d) => ({ ...d, staff_required: parseInt(d.staff_required, 10), notes: d.notes || null })),
      });
      toast.success('Staffing request submitted — our team has been notified');
      navigate('/client/requests');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ClientLayout>
      <div className="space-y-4" data-testid="client-new-request">
        <button
          type="button"
          onClick={() => (category ? setCategory(null) : navigate('/client/requests'))}
          className="flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900"
          data-testid="new-request-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> {category ? 'Change category' : 'Back to requests'}
        </button>

        {!category ? (
          <>
            <div>
              <h1 className="text-xl sm:text-3xl font-bold text-zinc-900 tracking-tight">Create Staffing Request</h1>
              <p className="text-sm text-zinc-500 mt-0.5">What type of staff do you need?</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setCategory(c.key)}
                  data-testid={`category-${c.key}`}
                  className="bg-white border border-zinc-200 rounded-lg shadow-sm p-5 text-left hover:border-primary hover:shadow-md active:scale-[0.99] transition-all"
                >
                  <span className="w-11 h-11 rounded-full bg-[#14181f] flex items-center justify-center">
                    <c.icon className="w-5 h-5 text-primary" strokeWidth={1.75} />
                  </span>
                  <p className="mt-3 text-base font-bold text-zinc-900">{c.label}</p>
                  <p className="mt-1 text-xs text-zinc-500 leading-relaxed">{c.desc}</p>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div>
              <h1 className="text-xl sm:text-3xl font-bold text-zinc-900 tracking-tight capitalize">{category} Staffing Request</h1>
              <p className="text-sm text-zinc-500 mt-0.5">Fill in the job details, then add one row per date &amp; position.</p>
            </div>

            {/* General details */}
            <Card className="bg-white border-zinc-200 rounded-lg shadow-sm">
              <CardContent className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider">{SITE_LABEL[category]} *</Label>
                  <Input value={form.site_name} onChange={(e) => setForm({ ...form, site_name: e.target.value })} className="rounded-md" data-testid="req-site-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider">Full Address *</Label>
                  <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-md" data-testid="req-address" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider">Postcode *</Label>
                  <Input value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} className="rounded-md" data-testid="req-postcode" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider">Contact Person *</Label>
                  <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="rounded-md" data-testid="req-contact-person" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider">Contact Number *</Label>
                  <Input value={form.contact_number} onChange={(e) => setForm({ ...form, contact_number: e.target.value })} className="rounded-md" data-testid="req-contact-number" />
                </div>
                {category === 'labour' && (
                  <>
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-wider">Purchase Order (optional)</Label>
                      <Input value={form.purchase_order} onChange={(e) => setForm({ ...form, purchase_order: e.target.value })} className="rounded-md" data-testid="req-po" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-wider">Reference Number (optional)</Label>
                      <Input value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} className="rounded-md" data-testid="req-ref" />
                    </div>
                  </>
                )}
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider">Additional Information</Label>
                  <Textarea rows={2} value={form.additional_info} onChange={(e) => setForm({ ...form, additional_info: e.target.value })} className="rounded-md" data-testid="req-info" />
                </div>
              </CardContent>
            </Card>

            {/* Day rows */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-sm font-semibold text-zinc-900">Dates &amp; Staff ({days.length} row{days.length !== 1 ? 's' : ''} · {totalStaff} staff)</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setRangeOpen((v) => !v)} className="rounded-md border-zinc-300 text-xs" data-testid="add-range-btn">
                  <CalendarRange className="w-3.5 h-3.5 mr-1" /> Add Date Range
                </Button>
                <Button size="sm" variant="outline" onClick={addDay} className="rounded-md border-zinc-300 text-xs" data-testid="add-day-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Day
                </Button>
              </div>
            </div>

            {rangeOpen && (
              <RangeGenerator positions={positions} onGenerate={applyGeneratedRows} onCancel={() => setRangeOpen(false)} />
            )}

            <div className="space-y-2.5">
              {days.map((d, i) => (
                <Card key={i} className="bg-white border-zinc-200 rounded-lg shadow-sm" data-testid={`day-row-${i}`}>
                  <CardContent className="p-3 sm:p-4">
                    <div className="grid grid-cols-2 sm:grid-cols-6 gap-2.5 items-end">
                      <div className="space-y-1">
                        <Label className="text-[10px] uppercase tracking-wider">Date *</Label>
                        <Input type="date" value={d.date} onChange={(e) => setDay(i, { date: e.target.value })} className="rounded-md h-9 text-sm" data-testid={`day-date-${i}`} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] uppercase tracking-wider">Start *</Label>
                        <Input type="time" value={d.start_time} onChange={(e) => setDay(i, { start_time: e.target.value })} className="rounded-md h-9 text-sm" data-testid={`day-start-${i}`} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] uppercase tracking-wider">End *</Label>
                        <Input type="time" value={d.end_time} onChange={(e) => setDay(i, { end_time: e.target.value })} className="rounded-md h-9 text-sm" data-testid={`day-end-${i}`} />
                      </div>
                      <div className="space-y-1 col-span-2 sm:col-span-1">
                        <Label className="text-[10px] uppercase tracking-wider">Position *</Label>
                        <Select value={d.position} onValueChange={(v) => setDay(i, { position: v })}>
                          <SelectTrigger className="rounded-md h-9 text-sm" data-testid={`day-position-${i}`}>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {positions.map((p) => (
                              <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] uppercase tracking-wider">Staff # *</Label>
                        <Input type="number" min="1" value={d.staff_required} onChange={(e) => setDay(i, { staff_required: e.target.value })} className="rounded-md h-9 text-sm" data-testid={`day-staff-${i}`} />
                      </div>
                      <div className="flex gap-2">
                        <Input placeholder="Notes" value={d.notes} onChange={(e) => setDay(i, { notes: e.target.value })} className="rounded-md h-9 text-sm flex-1" data-testid={`day-notes-${i}`} />
                        {days.length > 1 && (
                          <Button size="icon" variant="outline" onClick={() => removeDay(i)} className="h-9 w-9 rounded-md border-red-200 text-red-500 hover:bg-red-50 shrink-0" data-testid={`remove-day-${i}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Button
              onClick={submit}
              disabled={submitting}
              data-testid="submit-request-btn"
              className="w-full h-11 bg-[#14181f] text-white hover:bg-[#232a35] rounded-md uppercase tracking-[0.18em] font-semibold"
            >
              <Send className="w-4 h-4 mr-2" />
              {submitting ? 'Submitting…' : `Submit Request (${totalStaff} staff)`}
            </Button>
          </>
        )}
      </div>
    </ClientLayout>
  );
}
